import React from "react";
import { motion } from "framer-motion";
import { Award, ShieldCheck, Factory, GraduationCap } from "lucide-react";
import Navbar from "../../components/Navbar";

const haccpLevels = [
  {
    level: "HACCP Level 2",
    icon: ShieldCheck,
    short: "Foundation Level",
    description:
      "HACCP Level 2 is designed for food handlers and operational staff who are directly involved in food preparation, processing, storage, and distribution.",
    points: [
      "Introduction to food safety hazards",
      "Basic HACCP principles",
      "Personal hygiene & contamination control",
      "Safe food handling practices",
      "Legal responsibilities"
    ]
  },
  {
    level: "HACCP Level 3",
    icon: Factory,
    short: "Supervisory Level",
    description:
      "HACCP Level 3 targets supervisors, quality officers, and team leaders responsible for implementing and monitoring food safety systems.",
    points: [
      "Detailed hazard analysis",
      "Developing HACCP plans",
      "Monitoring & verification procedures",
      "Corrective actions",
      "Food safety management documentation"
    ]
  },
  {
    level: "HACCP Level 4",
    icon: GraduationCap,
    short: "Management Level",
    description:
      "HACCP Level 4 is for senior management, consultants, and auditors who design, review, and continuously improve HACCP systems.",
    points: [
      "Advanced HACCP system design",
      "Risk-based decision making",
      "Integration with ISO 22000 & FSMS",
      "Audit readiness & compliance",
      "Leadership in food safety culture"
    ]
  }
];

const HACCP_Levels = () => {
  return (
    <>
    <Navbar /> 
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            HACCP Certification Levels
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions provides internationally recognized HACCP training programs
            tailored to different organizational roles and responsibilities.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {haccpLevels.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, rotateX: 15 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12, scale: 1.03 }}
                className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100 group"
              >
                {/* Floating Badge */}
                <span className="absolute top-6 right-6 text-xs font-semibold px-3 py-1 rounded-full bg-[var(--bg-primary)] text-white shadow">
                  {item.short}
                </span>

                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[var(--bg-primary)] text-white mb-6 shadow-lg">
                  <Icon size={32} />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                  {item.level}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {item.description}
                </p>

                {/* Points */}
                <ul className="space-y-3 text-sm text-gray-700">
                  {item.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Award size={16} className="text-[var(--bg-primary)] mt-1" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 text-center"
        >
          <p className="text-gray-600 mb-6">
            Want to enroll your team in HACCP certification training?
          </p>
          <button className="px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all">
            Book HACCP Training
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default HACCP_Levels;
